import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  GridLegacy as Grid,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import {
  Book as JournalIcon,
  Timeline as TradesIcon,
  Assessment as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Download as DownloadIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';

interface TradingJournalUser {
  uid: string;
  email: string;
  displayName: string;
  totalTrades: number;
  lastTradeDate: any;
  firstTradeDate: any;
  totalPnL: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  isActive: boolean;
  averageTradeSize: number;
  bestTrade: number;
  worstTrade: number;
  tradingDays: number;
}

interface TradingJournalSummary {
  totalJournalUsers: number;
  activeJournalUsers: number;
  totalTrades: number;
  totalPnL: number;
  averageTradesPerUser: number;
  usageGrowthRate: number;
  profitableUsers: number;
  totalVolume: number;
}

interface TradeDetails {
  id: string;
  symbol: string;
  type: string;
  entryPrice: number;
  exitPrice?: number;
  quantity: number;
  pnl?: number;
  status: string;
  entryTime: any;
  broker: string;
  userEmail: string;
}

interface ChartData {
  name: string;
  value: number;
  percentage: string;
}

interface TradingAnalytics {
  symbolDistribution: ChartData[];
  brokerDistribution: ChartData[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const AdminTradingJournalReport: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [tradingJournalUsers, setTradingJournalUsers] = useState<TradingJournalUser[]>([]);
  const [tradingJournalSummary, setTradingJournalSummary] = useState<TradingJournalSummary>({
    totalJournalUsers: 0,
    activeJournalUsers: 0,
    totalTrades: 0,
    totalPnL: 0,
    averageTradesPerUser: 0,
    usageGrowthRate: 0,
    profitableUsers: 0,
    totalVolume: 0,
  });
  const [recentTrades, setRecentTrades] = useState<TradeDetails[]>([]);
  const [tradingAnalytics, setTradingAnalytics] = useState<TradingAnalytics>({
    symbolDistribution: [],
    brokerDistribution: [],
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedUser, setSelectedUser] = useState<TradingJournalUser | null>(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!userDoc.exists() || !userDoc.data().isAdmin) {
          setError('Access denied. Admin privileges required.');
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }

        setIsAdmin(true);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setError('Failed to verify admin status.');
      }
    };

    checkAdminStatus();
  }, [currentUser, navigate]);

  // Load trading journal data
  useEffect(() => {
    if (isAdmin) {
      loadTradingJournalData();
    }
  }, [isAdmin]);

  const loadTradingJournalData = async () => {
    try {
      setLoading(true);
      setError(null);
      setLoadingProgress(0);
      setLoadingMessage('Initializing data load...');

      console.log('Loading trading journal data with optimized parallel queries...');

      // Get all users first
      setLoadingMessage('Loading user accounts...');
      const usersQuery = collection(db, 'users');
      const usersSnapshot = await getDocs(usersQuery);
      console.log(`Found ${usersSnapshot.size} users`);
      setLoadingProgress(10);

      const usersMap = new Map();
      const userIds: string[] = [];
      
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        usersMap.set(doc.id, { 
          email: userData.email, 
          displayName: userData.displayName || userData.email 
        });
        userIds.push(doc.id);
      });

      // Process users in batches of 10 to avoid overwhelming Firebase
      const BATCH_SIZE = 10;
      const journalUsers: TradingJournalUser[] = [];
      const allRecentTrades: TradeDetails[] = [];
      const symbolCounts = new Map<string, number>();
      const brokerCounts = new Map<string, number>();
      let totalTrades = 0;
      let totalPnL = 0;
      let totalVolume = 0;
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Process users in parallel batches
      const totalBatches = Math.ceil(userIds.length / BATCH_SIZE);
      setLoadingMessage(`Processing user trading data...`);
      
      for (let i = 0; i < userIds.length; i += BATCH_SIZE) {
        const batch = userIds.slice(i, i + BATCH_SIZE);
        const currentBatch = Math.floor(i / BATCH_SIZE) + 1;
        setLoadingMessage(`Processing batch ${currentBatch}/${totalBatches} - Loading trading data for ${batch.length} users...`);

        // Create parallel promises for this batch
        const batchPromises = batch.map(async (userId) => {
          try {
            const userInfo = usersMap.get(userId);
            const userTradesQuery = collection(db, 'users', userId, 'trades');
            const userTradesSnapshot = await getDocs(userTradesQuery);
            
            if (userTradesSnapshot.empty) return null;

            const userTrades = userTradesSnapshot.docs.map(doc => ({ 
              id: doc.id, 
              ...doc.data() 
            })) as any[];

            // Collect symbol and broker data for analytics
            const userSymbolCounts = new Map<string, number>();
            const userBrokerCounts = new Map<string, number>();
            
            userTrades.forEach(trade => {
              if (trade.symbol) {
                const symbol = trade.symbol.toUpperCase();
                userSymbolCounts.set(symbol, (userSymbolCounts.get(symbol) || 0) + 1);
              }
              if (trade.broker) {
                const broker = trade.broker;
                userBrokerCounts.set(broker, (userBrokerCounts.get(broker) || 0) + 1);
              }
            });

            // Calculate user statistics
            const closedTrades = userTrades.filter(trade => trade.status === 'closed' && trade.pnl !== undefined);
            const winningTrades = closedTrades.filter(trade => trade.pnl > 0);
            const losingTrades = closedTrades.filter(trade => trade.pnl <= 0);
            const userTotalPnL = closedTrades.reduce((sum, trade) => sum + (trade.pnl || 0), 0);
            
            // Calculate volume
            const userVolume = userTrades.reduce((sum, trade) => {
              return sum + (trade.quantity || 0) * (trade.entryPrice || 0);
            }, 0);
            
            // Get first and last trade dates
            const sortedTrades = userTrades
              .filter(trade => trade.entryTime)
              .sort((a, b) => {
                const aTime = a.entryTime?.toDate ? a.entryTime.toDate() : new Date(a.entryTime);
                const bTime = b.entryTime?.toDate ? b.entryTime.toDate() : new Date(b.entryTime);
                return aTime.getTime() - bTime.getTime();
              });
            
            const firstTradeDate = sortedTrades[0]?.entryTime;
            const lastTradeDate = sortedTrades[sortedTrades.length - 1]?.entryTime;
            
            // Check if user is active (traded in last 30 days)
            let isActive = false;
            if (lastTradeDate) {
              const lastDate = lastTradeDate?.toDate ? lastTradeDate.toDate() : new Date(lastTradeDate);
              isActive = lastDate > thirtyDaysAgo;
            }

            // Calculate best and worst trades
            const pnlValues = closedTrades.map(trade => trade.pnl || 0).filter(pnl => pnl !== 0);
            const bestTrade = pnlValues.length > 0 ? Math.max(...pnlValues) : 0;
            const worstTrade = pnlValues.length > 0 ? Math.min(...pnlValues) : 0;

            // Calculate trading days
            const uniqueDays = new Set(
              userTrades.map(trade => {
                const date = trade.entryTime?.toDate ? trade.entryTime.toDate() : new Date(trade.entryTime);
                return date.toDateString();
              })
            );

            // Add recent trades to the global list (limit to 3 per user)
            const recentUserTrades = userTrades
              .sort((a, b) => {
                const aTime = a.entryTime?.toDate ? a.entryTime.toDate() : new Date(a.entryTime);
                const bTime = b.entryTime?.toDate ? b.entryTime.toDate() : new Date(b.entryTime);
                return bTime.getTime() - aTime.getTime();
              })
              .slice(0, 3)
              .map(trade => ({
                ...trade,
                userEmail: userInfo.email,
              }));

            return {
              journalUser: {
                uid: userId,
                email: userInfo.email,
                displayName: userInfo.displayName,
                totalTrades: userTrades.length,
                lastTradeDate: lastTradeDate,
                firstTradeDate: firstTradeDate,
                totalPnL: userTotalPnL,
                winningTrades: winningTrades.length,
                losingTrades: losingTrades.length,
                winRate: closedTrades.length > 0 ? (winningTrades.length / closedTrades.length) * 100 : 0,
                isActive: isActive,
                averageTradeSize: userTrades.length > 0 ? userVolume / userTrades.length : 0,
                bestTrade: bestTrade,
                worstTrade: worstTrade,
                tradingDays: uniqueDays.size,
              } as TradingJournalUser,
              recentTrades: recentUserTrades,
              stats: {
                trades: userTrades.length,
                pnl: userTotalPnL,
                volume: userVolume
              },
              symbolCounts: userSymbolCounts,
              brokerCounts: userBrokerCounts
            };

          } catch (userError) {
            console.error(`Error loading trades for user ${userId}:`, userError);
            return null;
          }
        });

        // Wait for all promises in this batch to complete
        const batchResults = await Promise.all(batchPromises);
        
                 // Process results
         batchResults.forEach(result => {
           if (result) {
             journalUsers.push(result.journalUser);
             allRecentTrades.push(...result.recentTrades);
             totalTrades += result.stats.trades;
             totalPnL += result.stats.pnl;
             totalVolume += result.stats.volume;
             
             // Collect analytics data
             if (result.symbolCounts) {
               result.symbolCounts.forEach((count, symbol) => {
                 symbolCounts.set(symbol, (symbolCounts.get(symbol) || 0) + count);
               });
             }
             if (result.brokerCounts) {
               result.brokerCounts.forEach((count, broker) => {
                 brokerCounts.set(broker, (brokerCounts.get(broker) || 0) + count);
               });
             }
           }
         });

         // Update progress
         const progress = 10 + (currentBatch / totalBatches) * 80; // 10% for user load, 80% for batches
         setLoadingProgress(Math.round(progress));

        // Add a small delay between batches to be nice to Firebase
        if (i + BATCH_SIZE < userIds.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      console.log(`Processed ${journalUsers.length} users with trades`);
      setLoadingMessage('Calculating summary statistics...');
      setLoadingProgress(95);

      // Create analytics data for charts
      const createChartData = (dataMap: Map<string, number>): ChartData[] => {
        const total = Array.from(dataMap.values()).reduce((sum, count) => sum + count, 0);
        return Array.from(dataMap.entries())
          .sort((a, b) => b[1] - a[1]) // Sort by count descending
          .slice(0, 10) // Take top 10
          .map(([name, value]) => ({
            name,
            value,
            percentage: ((value / total) * 100).toFixed(1)
          }));
      };

      const analytics: TradingAnalytics = {
        symbolDistribution: createChartData(symbolCounts),
        brokerDistribution: createChartData(brokerCounts),
      };

      // Calculate summary statistics
      const activeUsers = journalUsers.filter(user => user.isActive).length;
      const profitableUsers = journalUsers.filter(user => user.totalPnL > 0).length;
      
      const summary: TradingJournalSummary = {
        totalJournalUsers: journalUsers.length,
        activeJournalUsers: activeUsers,
        totalTrades: totalTrades,
        totalPnL: totalPnL,
        averageTradesPerUser: journalUsers.length > 0 ? totalTrades / journalUsers.length : 0,
        usageGrowthRate: journalUsers.length > 0 ? (activeUsers / journalUsers.length) * 100 : 0,
        profitableUsers: profitableUsers,
        totalVolume: totalVolume,
      };

      // Sort recent trades by date
      const sortedRecentTrades = allRecentTrades
        .sort((a, b) => {
          const aTime = a.entryTime?.toDate ? a.entryTime.toDate() : new Date(a.entryTime);
          const bTime = b.entryTime?.toDate ? b.entryTime.toDate() : new Date(b.entryTime);
          return bTime.getTime() - aTime.getTime();
        })
        .slice(0, 50); // Increased limit for recent trades

      setTradingJournalUsers(journalUsers);
      setTradingJournalSummary(summary);
      setRecentTrades(sortedRecentTrades);
      setTradingAnalytics(analytics);
      setLoadingProgress(100);
      setLoadingMessage('Complete!');

      if (journalUsers.length === 0) {
        console.log('No trading journal users found - this could be normal if no users have created trades yet');
        setError('No trading journal entries found. This could be because no users have created trades yet, or there might be a data access issue.');
      } else {
        setSuccess(`Successfully loaded ${journalUsers.length} trading journal users with ${totalTrades} total trades.`);
      }

    } catch (error) {
      console.error('Error loading trading journal data:', error);
      setError('Failed to load trading journal data: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = () => {
    try {
      const csvContent = generateCSVReport();
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trading_journal_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSuccess('Trading Journal report exported successfully.');
    } catch (error) {
      console.error('Error exporting report:', error);
      setError('Failed to export report.');
    }
  };

  const generateCSVReport = () => {
    const headers = [
      'User ID',
      'Email',
      'Display Name',
      'Total Trades',
      'Win Rate %',
      'Total P&L',
      'Winning Trades',
      'Losing Trades',
      'Best Trade',
      'Worst Trade',
      'Average Trade Size',
      'Trading Days',
      'Status',
      'First Trade',
      'Last Trade'
    ];

    const csvRows = ['TRADING JOURNAL REPORT', headers.join(',')];

    tradingJournalUsers.forEach(user => {
      const row = [
        user.uid,
        user.email,
        user.displayName || '',
        user.totalTrades,
        user.winRate.toFixed(1),
        user.totalPnL.toFixed(2),
        user.winningTrades,
        user.losingTrades,
        user.bestTrade.toFixed(2),
        user.worstTrade.toFixed(2),
        user.averageTradeSize.toFixed(2),
        user.tradingDays,
        user.isActive ? 'Active' : 'Inactive',
        user.firstTradeDate ? new Date(user.firstTradeDate.toDate()).toLocaleDateString() : 'Never',
        user.lastTradeDate ? new Date(user.lastTradeDate.toDate()).toLocaleDateString() : 'Never'
      ];
      csvRows.push(row.join(','));
    });

    return csvRows.join('\n');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleViewUserDetails = (user: TradingJournalUser) => {
    setSelectedUser(user);
    setUserDetailsOpen(true);
  };

  const formatDate = (date: any) => {
    if (!date) return 'Never';
    const dateObj = date?.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString();
  };

  const formatDateTime = (date: any) => {
    if (!date) return 'Never';
    const dateObj = date?.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <CircularProgress size={60} variant="determinate" value={loadingProgress} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading Trading Journal Data...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {loadingMessage}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {loadingProgress}% complete
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Using optimized parallel loading for better performance
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 4 }}>
            {error || 'Access denied. Admin privileges required.'}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <JournalIcon sx={{ fontSize: 40 }} />
              Trading Journal Analytics
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Comprehensive analysis of trading journal usage and performance
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadTradingJournalData}
              disabled={loading}
            >
              Refresh Data
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportReport}
              size="large"
            >
              Export Report
            </Button>
          </Box>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 4 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Total Users
                    </Typography>
                    <Typography variant="h4">
                      {tradingJournalSummary.totalJournalUsers}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <PeopleIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Active Users
                    </Typography>
                    <Typography variant="h4">
                      {tradingJournalSummary.activeJournalUsers}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last 30 days
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <AnalyticsIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Total Trades
                    </Typography>
                    <Typography variant="h4">
                      {tradingJournalSummary.totalTrades.toLocaleString()}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <TradesIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Total P&L
                    </Typography>
                    <Typography 
                      variant="h4"
                      color={tradingJournalSummary.totalPnL >= 0 ? 'success.main' : 'error.main'}
                    >
                      {tradingJournalSummary.totalPnL >= 0 ? '+' : ''}${tradingJournalSummary.totalPnL.toFixed(0)}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: tradingJournalSummary.totalPnL >= 0 ? 'success.main' : 'error.main' }}>
                    {tradingJournalSummary.totalPnL >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Additional Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="primary.main" gutterBottom>
                  {Math.round(tradingJournalSummary.averageTradesPerUser)}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Avg Trades per User
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="success.main" gutterBottom>
                  {tradingJournalSummary.profitableUsers}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Profitable Users
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="info.main" gutterBottom>
                  {Math.round(tradingJournalSummary.usageGrowthRate)}%
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Activity Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs for different views */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Users Overview" />
              <Tab label="Recent Trades" />
              <Tab label="Performance Analysis" />
              <Tab label="Analytics & Charts" />
            </Tabs>
          </Box>

          <TabPanel value={activeTab} index={0}>
            {/* Users Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Trades</TableCell>
                    <TableCell>Win Rate</TableCell>
                    <TableCell>Total P&L</TableCell>
                    <TableCell>Best Trade</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Trade</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tradingJournalUsers.map((user) => (
                    <TableRow key={user.uid} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {user.displayName?.charAt(0) || user.email.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {user.displayName || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {user.totalTrades}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.tradingDays} days
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          fontWeight="medium"
                          color={user.winRate >= 50 ? 'success.main' : 'error.main'}
                        >
                          {user.winRate.toFixed(1)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.winningTrades}W/{user.losingTrades}L
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          fontWeight="medium"
                          color={user.totalPnL >= 0 ? 'success.main' : 'error.main'}
                        >
                          {user.totalPnL >= 0 ? '+' : ''}${user.totalPnL.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          fontWeight="medium"
                          color="success.main"
                        >
                          ${user.bestTrade.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={user.isActive ? 'Active' : 'Inactive'}
                          color={user.isActive ? 'success' : 'default'}
                          variant={user.isActive ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDate(user.lastTradeDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => handleViewUserDetails(user)}
                        >
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {/* Recent Trades */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Entry Price</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>P&L</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentTrades.map((trade) => (
                    <TableRow key={trade.id} hover>
                      <TableCell>
                        <Typography variant="caption">
                          {trade.userEmail}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {trade.symbol}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={trade.type?.toUpperCase()}
                          color={trade.type === 'buy' ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{trade.entryPrice?.toFixed(5)}</TableCell>
                      <TableCell>{trade.quantity}</TableCell>
                      <TableCell>
                        {trade.pnl !== undefined ? (
                          <Typography 
                            variant="body2" 
                            color={trade.pnl >= 0 ? 'success.main' : 'error.main'}
                            fontWeight="medium"
                          >
                            {trade.pnl >= 0 ? '+' : ''}${trade.pnl.toFixed(2)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={trade.status}
                          color={trade.status === 'closed' ? 'success' : 'primary'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDateTime(trade.entryTime)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            {/* Performance Analysis */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top Performers (by P&L)
                    </Typography>
                    <List>
                      {tradingJournalUsers
                        .sort((a, b) => b.totalPnL - a.totalPnL)
                        .slice(0, 5)
                        .map((user, index) => (
                          <ListItem key={user.uid} divider>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2">
                                    #{index + 1} {user.displayName || user.email}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    fontWeight="bold"
                                    color="success.main"
                                  >
                                    +${user.totalPnL.toFixed(2)}
                                  </Typography>
                                </Box>
                              }
                              secondary={`${user.totalTrades} trades • ${user.winRate.toFixed(1)}% win rate`}
                            />
                          </ListItem>
                        ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Most Active Traders
                    </Typography>
                    <List>
                      {tradingJournalUsers
                        .sort((a, b) => b.totalTrades - a.totalTrades)
                        .slice(0, 5)
                        .map((user, index) => (
                          <ListItem key={user.uid} divider>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2">
                                    #{index + 1} {user.displayName || user.email}
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {user.totalTrades} trades
                                  </Typography>
                                </Box>
                              }
                              secondary={`${user.tradingDays} trading days • Last: ${formatDate(user.lastTradeDate)}`}
                            />
                          </ListItem>
                        ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            {/* Analytics & Charts */}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Trading Analytics Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Visual breakdown of trading patterns and broker preferences
                </Typography>
              </Grid>
              
              {/* Symbol Distribution Chart */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Most Traded Symbols
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Distribution of currency pairs and instruments
                    </Typography>
                    {tradingAnalytics.symbolDistribution.length > 0 ? (
                      <Box sx={{ height: 400 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={tradingAnalytics.symbolDistribution}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percentage }) => `${name} (${percentage}%)`}
                              outerRadius={120}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {tradingAnalytics.symbolDistribution.map((entry, index) => (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={`hsl(${(index * 137.5) % 360}, 70%, 50%)`} 
                                />
                              ))}
                            </Pie>
                            <Tooltip 
                              formatter={(value: any, name: any) => [
                                `${value} trades`,
                                'Count'
                              ]}
                            />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          No symbol data available
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Broker Distribution Chart */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Broker Usage Distribution
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Popular brokers among traders
                    </Typography>
                    {tradingAnalytics.brokerDistribution.length > 0 ? (
                      <Box sx={{ height: 400 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={tradingAnalytics.brokerDistribution}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percentage }) => `${name} (${percentage}%)`}
                              outerRadius={120}
                              fill="#82ca9d"
                              dataKey="value"
                            >
                              {tradingAnalytics.brokerDistribution.map((entry, index) => (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={`hsl(${(index * 137.5 + 60) % 360}, 70%, 50%)`} 
                                />
                              ))}
                            </Pie>
                            <Tooltip 
                              formatter={(value: any, name: any) => [
                                `${value} trades`,
                                'Count'
                              ]}
                            />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          No broker data available
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Summary Statistics */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Analytics Summary
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Symbol Diversity
                        </Typography>
                        <Typography variant="h4" color="primary.main">
                          {tradingAnalytics.symbolDistribution.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Unique symbols traded
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Broker Count
                        </Typography>
                        <Typography variant="h4" color="secondary.main">
                          {tradingAnalytics.brokerDistribution.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Different brokers used
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Card>

        {/* User Details Dialog */}
        <Dialog 
          open={userDetailsOpen} 
          onClose={() => setUserDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            User Trading Details: {selectedUser?.displayName || selectedUser?.email}
          </DialogTitle>
          <DialogContent>
            {selectedUser && (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" gutterBottom>Statistics</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Total Trades" secondary={selectedUser.totalTrades} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Win Rate" secondary={`${selectedUser.winRate.toFixed(1)}%`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Total P&L" secondary={`$${selectedUser.totalPnL.toFixed(2)}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Best Trade" secondary={`$${selectedUser.bestTrade.toFixed(2)}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Worst Trade" secondary={`$${selectedUser.worstTrade.toFixed(2)}`} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" gutterBottom>Activity</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Trading Days" secondary={selectedUser.tradingDays} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Avg Trade Size" secondary={`$${selectedUser.averageTradeSize.toFixed(2)}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="First Trade" secondary={formatDate(selectedUser.firstTradeDate)} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Last Trade" secondary={formatDate(selectedUser.lastTradeDate)} />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Status" 
                        secondary={selectedUser.isActive ? 'Active' : 'Inactive'}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUserDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}; 